import { COMMON, ifyouApi, kevinAuthAPI, kevinCommonAPI } from 'apis/config';
import { useQuery } from '@tanstack/react-query';
import ErrorMessage from 'apis/ErrorMessage';
import { IDropListProps } from 'utils/types/common';
import { handleMessage, showApiErrorMessage } from 'utils/helper/message';
import { message } from 'antd';
import {
  ICommonCode,
  INumberCodeQueryResult,
  IStringCodeQueryResult,
} from 'utils/types/storyTypes';

// * 2023.12
// * useQuery 사용 주의점 : get 해올때 res.data 리턴받을것!!

// 프로젝트의 능력 리스트 조회
const fetchProjectAbilityList = (projectID) =>
  kevinCommonAPI
    .get(`/common/profile/${projectID}/ability/a`)
    .then((res) => res.data);

// 프로젝트의 코드리스트 조회
const fetchProjectCodeList = (projectID, codeType, dlcID = -1) =>
  kevinCommonAPI
    .get(`/common/project/${projectID}/${codeType}/${dlcID}`)
    .then((res) => res.data);

// 프로젝트의 모델 리스트 조회
const fetchProjectModels = (projectID) =>
  kevinCommonAPI
    .get(`/common/project/${projectID}/model/-1`)
    .then((res) => res.data);

// 공용 말풍선 세트 조회
const fetchBubbleDropdown = (lang) =>
  kevinCommonAPI.get(`/common/etc/bubble?lang=${lang}`).then((res) => res.data);

const fetchAppLangDropdown = () =>
  kevinCommonAPI.get(`/common/app-lang`).then((res) => res.data);

const fetchProduceLangDropdown = () =>
  kevinCommonAPI.get(`/common/produce-lang`).then((res) => res.data);

// 언어별 드롭다운 정보
const fetchLocalizedDropdown = (codeType, lang) =>
  kevinCommonAPI
    .get(`/common/localize/${codeType}?lang=${lang}`)
    .then((res) => res.data);

// 프로젝트 어빌리티 리스트
export const queryProjectAbilityList = (projectID): IStringCodeQueryResult => {
  const { isLoading, isError, data } = useQuery(
    ['project_ability', projectID],
    () => fetchProjectAbilityList(projectID),
    {},
  );

  if (!isLoading) {
    return { isLoading, isError, data };
  } else return { isLoading, isError, data: [] };
};

// 프로젝트 코드 리스트 조회 (캐릭터, 에피소드 등등.. 주로 dropdown 용도)
export const queryProjectCodeList = (
  projectID,
  codeType,
  dlcID = -1,
): IStringCodeQueryResult => {
  // console.log(`queryProjectModels call : `, projectID);

  const { isLoading, isError, data } = useQuery(
    ['project_codes', projectID, codeType, dlcID],
    () => fetchProjectCodeList(projectID, codeType, dlcID), // data를 리턴하도록 꼭 처리할것
  );

  // console.log(`queryProjectCodeList ${codeType} : `, isLoading);

  if (!isLoading) {
    // console.log(`queryProjectCodeList : `, data);
    return { isLoading, isError, data };
  } else return { isLoading, isError, data: [] };
}; // ? queryProjectCodeList

// ! 공용 드롭다운(언어별 번역)
export const queryLocalizedCodeList = (
  codeType,
  lang = 'KO',
): IStringCodeQueryResult => {
  // * 2023.12 useQuery를 아래와 같이 변경한다.
  // 무조건 isLoading, isError, data를 함께 주도록 변경

  const { isLoading, isError, data } = useQuery(
    ['local_codes', codeType, lang],
    () => fetchLocalizedDropdown(codeType, lang),
  );
  // console.log(`queryLocalizedCodeList : `, isLoading);

  if (!isLoading) {
    // console.log(`queryLocalizedCodeList : `, data);
    return { isLoading, isError, data }; // 완료
  } else return { isLoading, isError, data: [] }; // 대기중
}; // ? END queryLocalizedCodeList

// 프로젝트에 등록된 캐릭터 모델
export const queryProjectModels = (projectID): IStringCodeQueryResult => {
  // console.log(`queryProjectModels call : `, projectID);

  const { isLoading, isError, data } = useQuery(
    ['project_models', projectID],
    () => fetchProjectModels(projectID),
    {},
  );

  if (!isLoading) {
    return { isLoading, isError, data }; // 완료
  } else return { isLoading, isError, data: [] }; // 대기중
};

// 제작 가능 언어
export const queryProduceLang = () => {
  const { data, error } = useQuery(
    ['appLang'],
    async () => await fetchAppLangDropdown(),
    {},
  );

  if (error) {
    console.log(error);
    handleMessage(message, 'error', JSON.stringify(error));
  }

  return { data };
};

// 앱 언어
export const queryAppLang = () => {
  const { data, error } = useQuery(
    ['produceLang'],
    async () => await fetchProduceLangDropdown(),
    {},
  );

  if (error) {
    console.log(error);
    handleMessage(message, 'error', JSON.stringify(error));
  }

  return { data };
};

// * 유효한 말풍선 리스트
export const queryDropdownBubbleList = (lang = 'KO') => {
  const { data, error } = useQuery(
    ['bubbleList', lang],
    async () => await fetchBubbleDropdown(lang),
    {},
  );

  if (error) {
    console.log(error);
    handleMessage(message, 'error', JSON.stringify(error));
  }

  return { data };
};

export const fetchKevinStandards = (lang: string) =>
  kevinCommonAPI
    .get(`/common/kevin/standards?lang=${lang}`)
    .then((res) => res.data);

/// 다국어 리스트
export const fetchLocalizingList = () =>
  kevinCommonAPI.get(`${COMMON}/app-lang`).then((res) => res.data);

/// 로컬라이징 값 출력
export const fetchLocalTextMatching = (textId) =>
  ifyouApi
    .post(`${COMMON}`, {
      func: 'getKoreanText',
      text_id: textId,
      // text_id: 6300,
    })
    .then((res) => res.data);

/// 공개된 작품 리스트
export const fetchPublicProjectList = () =>
  ifyouApi
    .post(`${COMMON}`, { func: 'getPublicProjectDropdown' })
    .then((res) => {
      const newData: IDropListProps[] = [];
      res.data?.map((v, i) =>
        newData.push({ key: i, text: v.project_name, value: v.project_id }),
      );
      return newData;
    })
    .catch((error) => {
      if (error.response) {
        ErrorMessage(error.response.status, error.response.data);
      }
    });

// 작품 드롭다운 목록 - project_id, title (아래걸로 적용 후 삭제해야함. 22.11.SJY)
export const fetchProjectList = () =>
  ifyouApi
    .post(`${COMMON}`, { func: 'adminProjectList' })
    .then((res) => {
      const newData: IDropListProps[] = [];
      res.data?.map((v, i) =>
        newData.push({ key: i, text: v.title, value: v.project_id }),
      );
      return newData;
    })
    .catch((error) => {
      if (error.response) {
        ErrorMessage(error.response.status, error.response.data);
      }
    });

// 작품 드롭다운 목록 - project_id, title만 출력
export const fetchProjectDropApi = () =>
  ifyouApi
    .post(`${COMMON}`, {
      func: 'adminProjectList',
    })
    .then((res) => res.data);

/// 연결된 에피소드 리스트
export const fetchDropEpisodeList = (projectId) =>
  ifyouApi
    .post(`${COMMON}`, {
      func: 'getAllEpisodeListForDropdown',
      project_id: projectId,
    })
    .then((res) => {
      const newArr: IDropListProps[] = [];
      res?.data?.map((v, i) =>
        newArr.push({
          key: i,
          value: v.episode_id,
          text: v.episode_name,
        }),
      );
      return newArr;
    });

export const fetchProductList = () =>
  ifyouApi
    .post(`${COMMON}`, {
      func: 'standard',
      code: 'product',
    })
    .then((res) => {
      const newData: IDropListProps[] = [];
      res.data?.map((v, i) =>
        newData.push({ key: i, text: v.code_name, value: v.code }),
      );
      return newData;
    })
    .catch((error) => {
      if (error.response) {
        ErrorMessage(error.response.status, error.response.data);
      }
    });

// 상품 종류 드롭다운 (판매-인앱상점관리)
export const fetchProductTypeList = () =>
  ifyouApi
    .post(`${COMMON}`, { func: 'standard', code: 'product_type' })
    .then((res) => {
      const newData: IDropListProps[] = [];
      res.data?.map((v, i) =>
        newData.push({ key: i, text: v.code_name, value: v.code }),
      );
      return newData;
    })
    .catch((error) => {
      if (error.response) {
        ErrorMessage(error.response.status, error.response.data);
      }
    });

/** 이미지 드래깅 업로드 */
export const uploadApi = (projectId, formData) =>
  ifyouApi
    .post(`upload/${projectId}/common-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => res.data)
    .catch((error) => {
      if (error.response) {
        ErrorMessage(error.response.status, error.response.data);
      }
    });

/// 재화 목록 드롭다운
export const fetchCurrencyListApi = (word?: string, kind?: string) =>
  ifyouApi
    .post(`${COMMON}`, {
      func: 'getAllCurrencyList',
      // word: word,
      // kind: kind,
    })
    .then((res) => {
      const newData: any = [];
      res.data?.map((v, i) =>
        newData.push({
          key: i,
          text: v.origin_name,
          name: v.origin_name,
          value: v.currency,
          is_coin: v.is_coin,
        }),
      );
      return newData;
    });

/// 재화 목록 드롭다운 IF You
/**
 * 사용되는 곳
 * - 작품 관리-에피소드 수정
 * - 출석 보상 관리 등록 & 수정
 * - 판패 관리 - 코인 상점 관리 등록  & 수정
 * - code = 0 / 1 ( 1 이면 코인샵에서 사용 가능)
 */
export const fetchCurrencyDropApi = (word) =>
  ifyouApi
    .post(`${COMMON}`, {
      func: 'getCurrencyList',
      word: word,
      code: 1,
    })
    .then((res) => res.data);

/// 재화 드롭다운 (오토메)
export const fetchOtomeCurrencyDropApi = (word, projectId) =>
  ifyouApi
    .post(`${COMMON}`, {
      func: 'getOtomeCurrencyDropdown',
      word: word,
      project_id: projectId,
    })
    .then((res) => res.data);

// New localizingList api
export const fetchLocalizingListNew = () =>
  kevinAuthAPI.get(`${COMMON}/lang`).then((res) => res.data);

// new model list api
export const fetchModelList = (projectId, dlcId) => {
  return kevinAuthAPI
    .get(`/common/project/${projectId}/model/${dlcId}`)
    .then((res) => res.data);
};

// get item type
export const fetchItemTypeList = () => {
  return kevinAuthAPI.get(`/common/item_type`).then((res) => res.data);
};

// get Project model list
export const fetchProjectModelList = (projectId, dlcId) => {
  return kevinAuthAPI
    .get(`common/project/${projectId}/model/${dlcId}`)
    .then((res) => res.data);
};

// get Project minicut list
export const fetchProjectMinicutList = (projectId, dlcId) => {
  return kevinAuthAPI
    .get(`common/project/${projectId}/minicut/${dlcId}`)
    .then((res) => res.data);
};

// get ability list
export const fetchAbilityList = (projectId) => {
  return kevinAuthAPI
    .get(`/common/profile/${projectId}/ability/a`)
    .then((res) => res.data);
};

// get sale type list
export const fetchSaleTypeList = () => {
  return kevinAuthAPI.get(`/common/sale_type`).then((res) => res.data);
};

// get project bg list
export const fetchProjectBgList = (projectId, dlcId) => {
  return kevinAuthAPI
    .get(`/common/project/${projectId}/bg/${dlcId}`)
    .then((res) => res.data);
};

// get project bg list
export const fetchEpisodeList = (projectId, dlcId) => {
  return kevinAuthAPI
    .get(`/common/project/${projectId}/episode/${dlcId}`)
    .then((res) => res.data);
};
